import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

/**
 * Custom hook to handle LEGO-specific data and operations
 * @param {boolean} isWishlist - Whether this is for wishlist or collection
 * @returns {Object} LEGO data and functions
 */
const useLegoData = (isWishlist = false) => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search for LEGO sets
  const searchLegoSets = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const endpoint = '/lego/search';
      const response = await axiosInstance.get(endpoint, {
        params: { query }
      });

      if (response.data && Array.isArray(response.data.results)) {
        setSearchResults(response.data.results);
      } else {
        setSearchResults([]);
        setError('No LEGO sets found');
      }
    } catch (error) {
      console.error('Error searching LEGO sets:', error);
      setError('Failed to search LEGO sets');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Format LEGO set data for consistency
  const formatLegoSetData = useCallback((legoSet) => {
    return {
      setNum: legoSet.set_num || legoSet.setNum,
      name: legoSet.name,
      themeId: legoSet.theme_id?.toString() || legoSet.themeId?.toString() || '0',
      year: parseInt(legoSet.year) || new Date().getFullYear(),
      imageUrl: legoSet.set_img_url || legoSet.image_url || legoSet.imageUrl || '',
      set_img_url: legoSet.set_img_url || legoSet.image_url || legoSet.imageUrl || '',
      priority: legoSet.priority || 'Medium',
      notes: legoSet.notes || '',
      price: legoSet.price || ''
    };
  }, []);

  // Add LEGO set to collection or wishlist
  const addLegoSet = useCallback(async (legoSet) => {
    try {
      const formattedData = formatLegoSetData(legoSet);
      
      const endpoint = isWishlist ? '/lego-wishlist' : '/lego/add';
      const response = await axiosInstance.post(endpoint, formattedData);
      
      return response.data;
    } catch (error) {
      console.error('Error adding LEGO set:', error);
      throw error;
    }
  }, [isWishlist, formatLegoSetData]);

  // Get formatted image URL for LEGO sets
  const getFormattedImageUrl = useCallback((url) => {
    if (!url) return 'https://placehold.co/600x400?text=No+Image';
    
    // Check if the URL is already absolute (starts with http:// or https://)
    if (url.match(/^https?:\/\//i)) {
      // Replace HTTP with HTTPS to avoid mixed content warnings
      return url.replace(/^http:\/\//i, 'https://');
    }
    
    // If it's a relative URL, prepend the base URL
    if (url.startsWith('/')) {
      return `https://intercollect.app${url}`;
    }
    
    // Otherwise, return as is
    return url;
  }, []);

  return {
    searchResults,
    loading,
    error,
    searchLegoSets,
    formatLegoSetData,
    addLegoSet,
    getFormattedImageUrl,
    setError
  };
};

export default useLegoData;
